import type { ReactNode } from 'react'
import {
  FOUNDER_NAV_GROUPS,
  FOUNDER_NAV_HOME,
  FOUNDER_NAV_SETTINGS,
  type FounderNavEntry,
} from '../lib/founder-nav'
import { AppLayout } from './app-layout'
import type { NavBadgeValue, NavItem } from './app-sidebar'
import { BillingMenu, type BillingMenuProps } from './billing-menu'
import { StartupSwitcher, type StartupSwitcherItem } from './startup-switcher'
import { UserMenu, type UserMenuLink } from './user-menu'

export interface FounderLayoutProps {
  children: ReactNode
  activeNav: string
  /** Base path prefix for all navigation links (e.g. "/app/startup/abc123") */
  basePath?: string
  /** Whether user is eligible for a free promo session */
  promoSession?: boolean
  /** Pay-as-you-go balances shown in the topbar wallet popover */
  billing?: Omit<BillingMenuProps, 'billingTo'>
  /** Outreach contacts awaiting the founder's review; shows a topbar CTA when > 0 */
  /**
   * Nav badges keyed by item label. Badges show runway or news, never backlog —
   * see the outreach nav-badge rules for what earns one.
   */
  navBadges?: Record<string, NavBadgeValue>
  userName?: string
  userEmail?: string
  onSignOut?: () => void
  onSendFeedback?: () => void
  activeStartup?: StartupSwitcherItem
  startups?: StartupSwitcherItem[]
  onSwitchStartup?: (startupId: string) => void
  logoSrc?: string
  /** Called when clicking the startup name — use for navigation to dashboard */
  onStartupNameClick?: () => void
  /** Extra links in user dropdown (e.g. admin link) */
  userMenuExtraLinks?: UserMenuLink[]
  /** Primary action pinned above Settings in the sidebar. */
  sidebarFooterSlot?: ReactNode
  /** Nav item labels to render disabled (e.g. when the founder has no pitch deck) */
  disabledNavLabels?: string[]
  /** Labels of sidebar items (or sub-items) to leave out: features that are not open yet. */
  hiddenNavLabels?: string[]
  /** Tooltip shown for disabled nav items. Falls back to the label. */
  disabledNavTooltip?: string
}

function getInitials(name?: string, email?: string): string {
  if (name) {
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  return email?.charAt(0).toUpperCase() ?? '?'
}

/** The child badge to show on a collapsed group: red first, then amber, then whatever is there. */
function mostUrgentBadge(badges: NavBadgeValue[]): NavBadgeValue | undefined {
  // Green is news, not a call to act, so it never lights a collapsed group.
  const actionable = badges.filter((b) => b.tone !== 'emerald')
  return (
    actionable.find((b) => b.tone === 'red') ??
    actionable.find((b) => b.tone === 'amber') ??
    actionable[0]
  )
}

export function FounderLayout({
  children,
  activeNav,
  basePath = '',
  promoSession,
  billing,
  navBadges,
  userName,
  userEmail,
  onSignOut,
  onSendFeedback,
  activeStartup,
  startups = [],
  onSwitchStartup,
  logoSrc,
  onStartupNameClick,
  userMenuExtraLinks,
  sidebarFooterSlot,
  disabledNavLabels,
  hiddenNavLabels,
  disabledNavTooltip,
}: FounderLayoutProps) {
  const displayName = userName ?? 'User'
  const displayEmail = userEmail ?? ''

  const defaultStartup: StartupSwitcherItem = {
    id: 'default',
    name: 'My Startup',
    initials: 'MS',
  }

  const disabledSet = new Set(disabledNavLabels ?? [])
  const hiddenSet = new Set(hiddenNavLabels ?? [])

  const withBasePath = (entry: FounderNavEntry): NavItem => {
    const disabled = disabledSet.has(entry.label)
    const childBadges = (entry.children ?? []).flatMap((child) => navBadges?.[child.label] ?? [])
    return {
      ...entry,
      to: entry.to ? `${basePath}${entry.to}` : undefined,
      disabled,
      disabledTooltip: disabled ? disabledNavTooltip : undefined,
      // A collapsed group shows its most urgent child, so a flag inside it is never hidden.
      badge: navBadges?.[entry.label] ?? mostUrgentBadge(childBadges),
      children: entry.children
        ?.filter((child) => !hiddenSet.has(child.label))
        .map((child) => {
          // A gated section gates everything inside it — otherwise disabling
          // "Outreach" would still leave its campaign links clickable.
          const childDisabled = disabled || disabledSet.has(child.label)
          return {
            ...child,
            to: `${basePath}${child.to}`,
            disabled: childDisabled,
            disabledTooltip: childDisabled ? disabledNavTooltip : undefined,
            badge: navBadges?.[child.label],
          }
        }),
    }
  }

  const navItems = [FOUNDER_NAV_HOME, ...FOUNDER_NAV_GROUPS].map(withBasePath)
  const settingsItem = withBasePath(FOUNDER_NAV_SETTINGS)

  return (
    <AppLayout
      activeNav={activeNav}
      navItems={navItems}
      sidebarFooterItem={settingsItem}
      sidebarFooterSlot={sidebarFooterSlot}
      // Not collapsible: the two-level menu is the primary navigation, and an
      // icon-only rail can't show sub-items. Omitting `collapsible` drops the
      // toggle; `defaultCollapsed` keeps the column expanded.
      defaultCollapsed={false}
      logoSrc={logoSrc}
      // The dashboard is the app's root, and the brand is where everyone
      // reaches for it.
      homeTo={basePath}
      topbarLeft={
        <StartupSwitcher
          active={activeStartup ?? defaultStartup}
          startups={startups.length > 0 ? startups : [defaultStartup]}
          onSwitch={onSwitchStartup ?? (() => {})}
          onNameClick={onStartupNameClick}
        />
      }
      topbarNotifications={
        <div className="flex items-center gap-1">
          {promoSession && (
            <a
              href={`${basePath}/pitch`}
              className="flex items-center gap-1.5 rounded-lg bg-emerald-500/10 px-2.5 py-1.5 text-xs font-medium text-emerald-400 transition-colors hover:bg-emerald-500/20"
            >
              <span>Free AI Session</span>
              <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px]">
                limited time
              </span>
            </a>
          )}
          {billing && <BillingMenu {...billing} billingTo={`${basePath}/billing`} />}
        </div>
      }
      topbarRight={
        <UserMenu
          initials={getInitials(userName, userEmail)}
          name={displayName}
          email={displayEmail}
          profileTo={`${basePath}/me`}
          settingsTo={`${basePath}/preferences`}
          extraLinks={userMenuExtraLinks}
          onSignOut={onSignOut}
          onSendFeedback={onSendFeedback}
        />
      }
    >
      {children}
    </AppLayout>
  )
}
